/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Minimal jQuery shim for AEM clientlib compatibility.
 * Provides the subset of jQuery methods needed by clientlib-static-component.min.js
 * to parse and render modal overlays (e.g., sign-in modal).
 *
 * This uses native DOM APIs - no jQuery dependency required.
 */

declare global {
  interface Window {
    $: any;
    jQuery: any;
  }
}

class JQueryShim {
  private elements: Element[];

  constructor(selectorOrHtml: string | Element | Element[] | NodeList) {
    if (typeof selectorOrHtml === 'string') {
      if (selectorOrHtml.trim().startsWith('<')) {
        const template = document.createElement('template');
        template.innerHTML = selectorOrHtml.trim();
        this.elements = Array.from(template.content.children);
      } else {
        this.elements = Array.from(document.querySelectorAll(selectorOrHtml));
      }
    } else if (selectorOrHtml instanceof NodeList) {
      this.elements = Array.from(selectorOrHtml) as Element[];
    } else if (Array.isArray(selectorOrHtml)) {
      this.elements = selectorOrHtml;
    } else {
      this.elements = [selectorOrHtml];
    }
  }

  get length(): number {
    return this.elements.length;
  }

  [index: number]: Element;

  get(index: number): Element | undefined {
    return this.elements[index];
  }

  filter(selector: string): JQueryShim {
    const filtered = this.elements.filter(el => el.matches && el.matches(selector));
    return new JQueryShim(filtered);
  }

  find(selector: string): JQueryShim {
    const found: Element[] = [];
    this.elements.forEach(el => {
      found.push(...Array.from(el.querySelectorAll(selector)));
    });
    return new JQueryShim(found);
  }

  each(callback: (this: Element, index: number, element: Element) => void): JQueryShim {
    this.elements.forEach((el, i) => callback.call(el, i, el));
    return this;
  }

  attr(name: string, value?: string): string | undefined | JQueryShim {
    if (value !== undefined) {
      this.elements.forEach(el => el.setAttribute(name, value));
      return this;
    }
    return this.elements[0]?.getAttribute(name) ?? undefined;
  }

  text(value?: string): string | JQueryShim {
    if (value !== undefined) {
      this.elements.forEach(el => {
        el.textContent = value;
      });
      return this;
    }
    return this.elements[0]?.textContent ?? '';
  }

  html(value?: string): string | JQueryShim {
    if (value !== undefined) {
      this.elements.forEach(el => {
        el.innerHTML = value;
      });
      return this;
    }
    return this.elements[0]?.innerHTML ?? '';
  }

  append(content: Element | string | JQueryShim): JQueryShim {
    this.elements.forEach(el => {
      if (typeof content === 'string') {
        el.insertAdjacentHTML('beforeend', content);
      } else if (content instanceof JQueryShim) {
        content.elements.forEach(child => el.appendChild(child));
      } else {
        el.appendChild(content);
      }
    });
    return this;
  }

  prepend(content: Element | string | JQueryShim): JQueryShim {
    this.elements.forEach(el => {
      if (typeof content === 'string') {
        el.insertAdjacentHTML('afterbegin', content);
      } else if (content instanceof JQueryShim) {
        content.elements.forEach(child => el.insertBefore(child, el.firstChild));
      } else {
        el.insertBefore(content, el.firstChild);
      }
    });
    return this;
  }

  remove(): JQueryShim {
    this.elements.forEach(el => el.parentNode?.removeChild(el));
    return this;
  }

  addClass(className: string): JQueryShim {
    this.elements.forEach(el => el.classList.add(className));
    return this;
  }

  removeClass(className: string): JQueryShim {
    this.elements.forEach(el => el.classList.remove(className));
    return this;
  }

  hasClass(className: string): boolean {
    return this.elements[0]?.classList.contains(className) ?? false;
  }

  css(property: string, value?: string): string | JQueryShim {
    if (value !== undefined) {
      this.elements.forEach(el => {
        (el as HTMLElement).style.setProperty(property, value);
      });
      return this;
    }
    return getComputedStyle(this.elements[0] as HTMLElement).getPropertyValue(property);
  }

  on(event: string, handler: EventListener): JQueryShim {
    this.elements.forEach(el => el.addEventListener(event, handler));
    return this;
  }

  off(event: string, handler: EventListener): JQueryShim {
    this.elements.forEach(el => el.removeEventListener(event, handler));
    return this;
  }

  trigger(event: string): JQueryShim {
    this.elements.forEach(el => el.dispatchEvent(new Event(event)));
    return this;
  }

  val(value?: string): string | JQueryShim {
    if (value !== undefined) {
      this.elements.forEach(el => {
        (el as HTMLInputElement).value = value;
      });
      return this;
    }
    return (this.elements[0] as HTMLInputElement)?.value ?? '';
  }

  first(): JQueryShim {
    return new JQueryShim(this.elements[0] ? [this.elements[0]] : []);
  }

  last(): JQueryShim {
    return new JQueryShim(this.elements.length ? [this.elements[this.elements.length - 1]] : []);
  }

  eq(index: number): JQueryShim {
    return new JQueryShim(this.elements[index] ? [this.elements[index]] : []);
  }

  parent(): JQueryShim {
    const parents = this.elements.map(el => el.parentElement).filter(Boolean) as Element[];
    return new JQueryShim(parents);
  }

  children(selector?: string): JQueryShim {
    const children: Element[] = [];
    this.elements.forEach(el => {
      const kids = Array.from(el.children);
      if (selector) {
        children.push(...kids.filter(child => child.matches(selector)));
      } else {
        children.push(...kids);
      }
    });
    return new JQueryShim(children);
  }

  closest(selector: string): JQueryShim {
    const closest = this.elements[0]?.closest(selector);
    return new JQueryShim(closest ? [closest] : []);
  }

  data(key: string, value?: any): any {
    if (value !== undefined) {
      this.elements.forEach(el => {
        (el as HTMLElement).dataset[key] = typeof value === 'object' ? JSON.stringify(value) : value;
      });
      return this;
    }
    const dataValue = (this.elements[0] as HTMLElement)?.dataset[key];
    try {
      return dataValue ? JSON.parse(dataValue) : undefined;
    } catch {
      return dataValue;
    }
  }

  show(): JQueryShim {
    this.elements.forEach(el => {
      (el as HTMLElement).style.display = '';
    });
    return this;
  }

  hide(): JQueryShim {
    this.elements.forEach(el => {
      (el as HTMLElement).style.display = 'none';
    });
    return this;
  }

  is(selector: string): boolean {
    return this.elements[0]?.matches(selector) ?? false;
  }

  /**
   * Bootstrap modal method shim.
   * Handles 'show', 'hide', 'toggle' actions for modal dialogs.
   */
  modal(action?: string | Record<string, any>): JQueryShim {
    this.elements.forEach(el => {
      const htmlEl = el as HTMLElement;

      if (action === 'show') {
        htmlEl.classList.add('show');
        htmlEl.style.display = 'block';
        htmlEl.setAttribute('aria-hidden', 'false');
        document.body.classList.add('modal-open');
      } else if (action === 'hide') {
        htmlEl.classList.remove('show');
        htmlEl.style.display = 'none';
        htmlEl.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('modal-open');
      } else if (action === 'toggle') {
        if (htmlEl.classList.contains('show')) {
          this.modal('hide');
        } else {
          this.modal('show');
        }
      } else if (action === 'handleUpdate') {
        // No-op for handleUpdate
      } else if (typeof action === 'object' || action === undefined) {
        // Initialize modal (show by default when called without action)
        this.modal('show');
      }
    });

    return this;
  }
}

type JQueryFunction = {
  (selector: string | Element | Element[] | NodeList): JQueryShim;
  fn: Record<string, any>;
};

/**
 * Adds the modal method to jQuery's prototype if it doesn't exist.
 * This ensures Bootstrap-like modal functionality is available.
 */
function ensureModalMethod(): void {
  if (typeof window === 'undefined' || !window.$) return;

  if (!window.$.fn) {
    window.$.fn = {};
  }

  if (!window.$.fn.modal) {
    window.$.fn.modal = function (action?: string | Record<string, any>) {
      this.each(function (this: HTMLElement) {
        if (action === 'show') {
          this.classList.add('show');
          this.style.display = 'block';
          this.setAttribute('aria-hidden', 'false');
          document.body.classList.add('modal-open');
        } else if (action === 'hide') {
          this.classList.remove('show');
          this.style.display = 'none';
          this.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('modal-open');
        } else if (action === 'toggle') {
          if (this.classList.contains('show')) {
            window.$(this).modal('hide');
          } else {
            window.$(this).modal('show');
          }
        } else if (action === 'handleUpdate' || action === 'dispose') {
          // No-op for these actions
        } else if (typeof action === 'object' || action === undefined) {
          window.$(this).modal('show');
        }
      });

      return this;
    };
  }
}

/**
 * Sets up a watcher to add the modal method to jQuery if it loads after our shim.
 */
function setupJQueryWatcher(): void {
  if (typeof window === 'undefined') return;

  if ((window as any).__jqueryWatcherSetup) return;
  (window as any).__jqueryWatcherSetup = true;

  let pollCount = 0;
  const maxPolls = 50;

  const pollInterval = setInterval(() => {
    pollCount++;

    if (window.$ && window.$.fn) {
      ensureModalMethod();
    }

    if (window.jQuery && window.jQuery.fn && !window.jQuery.fn.modal) {
      window.jQuery.fn.modal = window.$.fn?.modal;
    }

    if (pollCount >= maxPolls || (window.$ && window.$.fn && window.$.fn.modal)) {
      clearInterval(pollInterval);
    }
  }, 100);
}

/**
 * Initializes the jQuery shim.
 * Sets up window.$ and window.jQuery with the minimal jQuery API.
 */
export function initJQueryShim(): void {
  if (typeof window === 'undefined') return;

  if (!window.$) {
    const $: JQueryFunction = (selector: string | Element | Element[] | NodeList) => new JQueryShim(selector);
    $.fn = {};
    window.$ = $;
    window.jQuery = window.$;
  } else {
    ensureModalMethod();
  }

  setupJQueryWatcher();
}
